import { useTheme } from '@/components/ThemeProvider';
import darkTextLogo from '@/assets/logos/kyck-logo-dark-text.svg';
import whiteTextLogo from '@/assets/logos/kyck-logo-white-text.svg';

/**
 * Kyck wordmark (Design System v1, Phase 2 Sub-phase A).
 *
 * Two artworks share the brand green (#48bc4b) but differ in text colour:
 * dark-navy text for light surfaces, white text for dark surfaces. The
 * `variant` defaults to `auto`, following the resolved theme; pass a fixed
 * variant for surfaces whose tone doesn't track the theme (e.g. a
 * permanently-dark sidebar in Phase 3).
 *
 * Not placed in any layout yet — created for Phase 3 to consume.
 */

/** Intrinsic aspect ratio of the artwork (viewBox 704.81335 × 310.82666). */
const ASPECT_RATIO = 704.81335 / 310.82666;

type LogoVariant = 'auto' | 'dark-text' | 'white-text';

export function KyckLogo({
  size = 32,
  variant = 'auto',
  className,
}: {
  /** Rendered height in px. Width is derived from the artwork aspect ratio. */
  size?: number;
  /** `auto` follows the resolved theme; force a variant for fixed-tone surfaces. */
  variant?: LogoVariant;
  className?: string;
}) {
  const { resolvedTheme } = useTheme();
  const useWhiteText =
    variant === 'white-text' || (variant === 'auto' && resolvedTheme === 'dark');

  return (
    <img
      src={useWhiteText ? whiteTextLogo : darkTextLogo}
      alt="Kyck"
      height={size}
      width={Math.round(size * ASPECT_RATIO)}
      className={className}
      draggable={false}
    />
  );
}
